/**
 * Route gate for the admin-only areas (/admin and /team).
 *
 * Team access rides the same `isAdmin` check for now (single-operator
 * deployment); when a `team-operator` role exists, this gate is where it
 * slots in — the layouts themselves stay role-agnostic.
 */
import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";

export function AdminGate({ children }: { children: ReactNode }) {
  // One snapshot, one query: `isAdmin` and its resolvedness come from the same
  // read. Deriving them from two `useAuth()` calls made them two independent
  // requests, and whenever the gate's "resolved" arrived before its "roles"
  // the branch below fired and dropped the operator on the dashboard.
  const { isAdmin } = useAuth();

  // `undefined` = not resolved yet (loading, retrying a post-deploy 502, or no
  // session at all). AuthGuard owns those surfaces; never redirect on them —
  // a redirect here is indistinguishable from a denial and loses the URL.
  if (isAdmin === undefined) {
    return (
      <div className="page">
        <div className="py-10 text-center text-ink-low text-sm">
          <span className="inline-block animate-pulse">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
